import { AnnatureForm, AnnatureSigner } from '@fastre/core/schemas/annature'
import {
    EsignForm,
    EsignFormType,
    getEsignFormFromListing,
    getFormName,
    getReiFormFromListing,
    isReiAndEsignFormType,
    ReiAndEsignFormType,
    SigningService,
} from '@fastre/core/schemas/esign'
import { formatAddress } from '@fastre/core/schemas/generic'
import { ReiForm } from '@fastre/core/schemas/rei'
import {
    AddRounded,
    Cancel,
    Check,
    DeleteRounded,
    MoreVertRounded,
    MoveToInbox,
    UploadFileRounded,
    Visibility,
} from '@mui/icons-material'
import {
    Box,
    Button,
    Dropdown,
    IconButton,
    Link,
    ListItemDecorator,
    Menu,
    MenuButton,
    MenuItem,
    Stack,
    Step,
    StepIndicator,
    Stepper,
    Typography,
} from '@mui/joy'
import { useApi } from 'api'
import { useUserData } from 'auth'
import PdfModal from 'components/pdfModal'
import { useShowSnack } from 'components/snackbar'
import { useOrgId } from 'helpers'
import OptionalTooltip from 'links/optionalTooltip'
import { prop, sortBy } from 'ramda'
import { useState } from 'react'
//import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
//import 'react-pdf/dist/esm/Page/TextLayer.css'
import { useUsersApi } from 'apiProviders'
import { docusignConnectionUrl } from 'settings/docusignConnectionResponse'
import ManualFormUploadModal from '../formStepper/uploadFormModal'
import { useListingContext } from '../listingProvider'
import AbandonFormModal from '../sharedModals/abandonFormModal'
import AddAttachmentModal from '../sharedModals/addAttachmentsModal'
import IframeModal from '../sharedModals/iframeModal'
import UploadFormModal from '../sharedModals/uploadFormModal'

enum FormStage {
    'voided' = -2,
    'declined' = -1,
    'none' = 0,
    'createdInRei',
    'created',
    'sent',
    'completed',
    'manual',
}

const getFormStage = (esignForm: EsignForm | undefined, reiForm: ReiForm | undefined): FormStage => {
    if (esignForm) {
        switch (esignForm.formStatus) {
            case 'draft':
            case 'created':
                return FormStage.created
            case 'sent':
            case 'delivered':
                return FormStage.sent
            case 'completed':
            case 'signed':
                return FormStage.completed
            case 'manual upload':
                return FormStage.manual
            case 'declined':
                return FormStage.declined
            case 'voided':
                return FormStage.voided
            default:
                return FormStage.none
        }
    }

    if (reiForm) {
        return FormStage.createdInRei
    }

    return FormStage.none
}

interface Props {
    title: string
    formType: EsignFormType | ReiAndEsignFormType
    createDisabled: boolean | string
    validate: () => Promise<boolean> | boolean
    preCreate: () => Promise<boolean> | boolean
    roleNames: string[]
    signingService?: SigningService
}

const DocusignFormStepper = ({
    title,
    formType,
    createDisabled,
    validate,
    preCreate,
    roleNames,
    signingService = 'annature',
}: Props) => {
    const { listing, refreshListing, setListing } = useListingContext()
    const { user } = useUserData()
    const showSnack = useShowSnack()
    const orgId = useOrgId()
    const usersApi = useUsersApi()

    const requiresRei = formType === 'contractForm' || formType == 'sellerDisclosureForm'
    const api = useApi()

    const [viewLoading, setViewLoading] = useState(false)
    const [loading, setLoading] = useState(false)
    const [previewPdf, setPreviewPdf] = useState<{ url: string; useAuth?: boolean; isRawPdf?: boolean }>()
    const [editUrl, setEditUrl] = useState<string>()
    const [showUploadForm, setShowUploadForm] = useState(false)
    const [showAbandonFormDialog, setShowAbandonFormDialog] = useState<
        EsignFormType | ReiAndEsignFormType | undefined
    >()
    const [showAddAttachmentsModal, setShowAddAttachmentsModal] = useState(false)
    const [showManualUploadModal, setShowManualUploadModal] = useState<
        EsignFormType | ReiAndEsignFormType | undefined
    >()

    const form = getEsignFormFromListing({ listing, formType })
    const reiForm = isReiAndEsignFormType(formType) ? getReiFormFromListing(listing, formType) : undefined

    console.log('reiForm', reiForm)

    const formStage = getFormStage(form, reiForm)

    const formName = getFormName(formType)

    const agent = usersApi.data?.find(user => user.userId == listing.agents[0].userId)

    const manualFileUpload = false
    const requiresDisclosure = formType == 'contractForm'
    const disclosureCreated = listing.sellerDisclosureForm != undefined
    const disclosureComplete =
        listing.sellerDisclosureForm?.esign?.formStatus == 'completed' ||
        listing.sellerDisclosureForm?.esign?.formStatus == 'manual upload'

    const createDisabledBoolean = typeof createDisabled == 'string' ? true : createDisabled

    const getServiceConnectionUrl = () => {
        if (signingService === 'docusign') {
            return docusignConnectionUrl(user)
        } else if (signingService === 'zoho') {
            return `/${orgId}/settings?tab=Zoho+Sign`
        }
        return `/${orgId}/settings`
    }

    const serviceName =
        signingService === 'docusign' ? 'DocuSign' : signingService === 'zoho' ? 'Zoho Sign' : 'Annature'

    const isServiceConnected = () => {
        if (signingService === 'docusign') {
            return user.docusign != undefined
        } else if (signingService === 'zoho') {
            return user.zohoConnected
        } else if (signingService === 'annature') {
            // Annature uses API key authentication, so it's always connected if configured
            return true
        }
        return false
    }

    // Type guard to check if action is a ZohoSignAction
    const isZohoSignAction = (action: any): action is any => {
        return 'action_id' in action && 'action_status' in action
    }

    // Type guard to check if action is an AnnatureSigner
    const isAnnatureSigner = (action: any): action is AnnatureSigner => {
        return 'id' in action && 'status' in action
    }

    // Helper function to get action status
    const getActionStatus = (action: any): string => {
        if (isZohoSignAction(action)) {
            return action.action_status
        } else if (isAnnatureSigner(action)) {
            return (action as AnnatureSigner).status
        }
        return 'unknown'
    }

    // Helper function to get action name
    const getActionName = (action: any): string => {
        if (isZohoSignAction(action)) {
            return action.recipient_name
        } else if (isAnnatureSigner(action)) {
            return (action as AnnatureSigner).name
        }
        return 'Unknown'
    }

    // Helper function to get action ID
    const getActionId = (action: any): string => {
        if (isZohoSignAction(action)) {
            return action.action_id
        } else if (isAnnatureSigner(action)) {
            return (action as AnnatureSigner).id
        }
        return 'unknown'
    }

    // Button Components
    const CreateReiFormButton = () => (
        <OptionalTooltip
            show={!agent?.reiConnected}
            title={
                agent?.userId == user.userId
                    ? 'Connect your Realworks account to create forms'
                    : 'Agent must connect their Realworks account to create forms'
            }
        >
            <Box>
                <Button
                    loading={loading}
                    onClick={async () => {
                        if (await validate()) {
                            setLoading(true)
                            const pass = await preCreate()
                            if (pass) {
                                const data = await api
                                    .post(
                                        `/listing/sale/${listing.listingId}/${formType.toLocaleLowerCase()}/createreiform`,
                                    )
                                    .then(prop('data'))
                                setListing(data.updatedListing)
                                window.open(data.url, '_blank')
                            }
                            setLoading(false)
                        }
                    }}
                    startDecorator={<AddRounded />}
                    disabled={createDisabledBoolean || !agent?.reiConnected}
                >
                    Create REI Form
                </Button>
            </Box>
        </OptionalTooltip>
    )

    const CreateEsignFormButton = () => (
        <OptionalTooltip
            show={requiresDisclosure && !disclosureComplete}
            title={`Seller Disclosure statement required`}
        >
            <Box>
                <Button
                    loading={loading}
                    onClick={async () => {
                        if (await validate()) {
                            setLoading(true)
                            const pass = await preCreate()
                            if (pass) {
                                if (formType === 'contractForm' || formType === 'sellerDisclosureForm') {
                                    setShowAddAttachmentsModal(true)
                                } else if (manualFileUpload) {
                                    setShowUploadForm(true)
                                } else {
                                    const createData = await api
                                        .post(
                                            `/listing/sale/${listing.listingId}/${signingService}/${formType.toLocaleLowerCase()}/create`,
                                        )
                                        .then(prop('data'))
                                    if (createData) {
                                        const { updatedListing, editUrl } = createData
                                        setListing(updatedListing)
                                        setEditUrl(editUrl)
                                    } else {
                                        await refreshListing()
                                    }
                                    showSnack(`${formName} created`, 'success')
                                }
                            }
                            setLoading(false)
                        }
                    }}
                    startDecorator={<AddRounded />}
                    disabled={
                        createDisabledBoolean ||
                        (manualFileUpload && !isServiceConnected()) ||
                        (requiresDisclosure && !disclosureComplete)
                    }
                >
                    {manualFileUpload ? 'Upload' : 'Create'}
                    {requiresRei ? ' eSign' : ''} Form
                </Button>
            </Box>
        </OptionalTooltip>
    )

    const ViewFormButton = () => (
        <Button
            variant="outlined"
            loading={viewLoading}
            onClick={async () => {
                switch (formStage) {
                    case FormStage.completed:
                    case FormStage.manual:
                        setViewLoading(true)
                        const url = await api
                            .post(
                                `${import.meta.env.VITE_APP_API_URL}/${orgId}/listing/sale/${listing.listingId}/${signingService}/viewcompletedform`,
                                {
                                    formType,
                                },
                            )
                            .then(prop('data'))
                        setPreviewPdf({
                            url,
                            useAuth: false,
                            //isRawPdf: true,
                        })
                        setViewLoading(false)
                        break
                    case FormStage.sent:
                        setViewLoading(true)
                        const sentUrl = await api
                            .post(
                                `${import.meta.env.VITE_APP_API_URL}/${orgId}/listing/sale/${listing.listingId}/${signingService}/viewsent`,
                                {
                                    formType,
                                },
                            )
                            .then(prop('data'))
                        setPreviewPdf({
                            url: sentUrl,
                            useAuth: false,
                            isRawPdf: true,
                        })
                        setViewLoading(false)
                        break
                    case FormStage.created:
                        setViewLoading(true)
                        const viewData = await api
                            .post(`/listing/sale/${listing.listingId}/${signingService}/edit`, {
                                formType,
                            })
                            .then(prop('data'))

                        switch (signingService) {
                            case 'docusign':
                                setEditUrl(viewData.url)
                                setViewLoading(false)
                                break
                            case 'zoho':
                                const embedUrl = viewData.url
                                setEditUrl(embedUrl)
                                setViewLoading(false)
                                break
                            case 'annature':
                                setEditUrl(viewData.url)
                                setViewLoading(false)
                                break
                        }
                        break
                    case FormStage.createdInRei:
                        setViewLoading(true)
                        const data = await api
                            .get(`listing/sale/${listing.listingId}/rei/${formType.toLocaleLowerCase()}`)
                            .then(prop('data'))
                        window.open(data.url, '_blank')
                        setViewLoading(false)
                        break
                    default:
                        setPreviewPdf({
                            url: `${import.meta.env.VITE_APP_API_URL}/${orgId}/${signingService}/${(form as AnnatureForm).id}/view`,
                            useAuth: true,
                        })
                        break
                }
            }}
            startDecorator={<Visibility />}
        >
            View Form
        </Button>
    )

    // HeaderSection Component
    const HeaderSection = () => (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                maxWidth: '600px',
            }}
        >
            <Typography level="h4">{title}</Typography>
            <Dropdown>
                <MenuButton
                    slots={{ root: IconButton }}
                    slotProps={{ root: { size: 'sm' } }}
                >
                    <MoreVertRounded fontSize="small" />
                </MenuButton>
                <Menu>
                    {formStage < FormStage.completed && (
                        <MenuItem
                            onClick={async () => {
                                setLoading(true)
                                const pass = await preCreate()
                                if (pass) {
                                    setShowManualUploadModal(formType)
                                }
                                setLoading(false)
                            }}
                        >
                            <ListItemDecorator>
                                <UploadFileRounded />
                            </ListItemDecorator>
                            Upload Completed Form
                        </MenuItem>
                    )}
                    {formStage != FormStage.none && (
                        <MenuItem onClick={() => setShowAbandonFormDialog(formType)}>
                            <ListItemDecorator>
                                <DeleteRounded />
                            </ListItemDecorator>
                            Abandon Form
                        </MenuItem>
                    )}
                </Menu>
            </Dropdown>
            <Box sx={{ flexGrow: 1 }} />
            {formStage != FormStage.none && (
                <Box>
                    <ViewFormButton />
                </Box>
            )}
            {(formStage == FormStage.none || (requiresRei && reiForm && !form)) && (
                <OptionalTooltip
                    show={typeof createDisabled == 'string'}
                    title={createDisabled as string}
                >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {requiresRei && !reiForm ? <CreateReiFormButton /> : <CreateEsignFormButton />}
                    </Box>
                </OptionalTooltip>
            )}
        </Box>
    )

    // BodySection Component
    const BodySection = () => {
        // Handle manual upload case similar to formStepper
        if (form?.formStatus == 'manual upload') {
            return (
                <Typography
                    level="body-lg"
                    sx={{ mt: 1 }}
                >
                    Form manually uploaded
                </Typography>
            )
        }

        return (
            <>
                {!isServiceConnected() && (
                    <Typography sx={{ mt: 2 }}>
                        <Link href={getServiceConnectionUrl()}>Connect</Link> for {serviceName} integration or{' '}
                        <Link
                            component="button"
                            onClick={() => setShowManualUploadModal(formType)}
                        >
                            upload form manually
                        </Link>
                    </Typography>
                )}
                {isServiceConnected() && formStage != FormStage.manual && (
                    <>
                        <Stepper
                            orientation="vertical"
                            sx={{ width: '100%', mt: 2 }}
                        >
                            {requiresDisclosure && (
                                <>
                                    <Step
                                        indicator={
                                            <StepIndicator
                                                variant={disclosureCreated ? 'solid' : 'soft'}
                                                color="primary"
                                            >
                                                {disclosureCreated ? <Check /> : undefined}
                                            </StepIndicator>
                                        }
                                        sx={{
                                            '&::after': {
                                                ...(disclosureCreated && {
                                                    bgcolor: 'primary.solidBg',
                                                }),
                                            },
                                        }}
                                    >
                                        <Typography>Create Disclosure Statement</Typography>
                                    </Step>
                                    <Step
                                        indicator={
                                            <StepIndicator
                                                variant={disclosureComplete ? 'solid' : 'soft'}
                                                color={disclosureCreated ? 'primary' : 'neutral'}
                                            >
                                                {disclosureComplete ? <Check /> : undefined}
                                            </StepIndicator>
                                        }
                                        sx={{
                                            '&::after': {
                                                ...(disclosureComplete && {
                                                    bgcolor: 'primary.solidBg',
                                                }),
                                            },
                                        }}
                                    >
                                        {listing.sellerDisclosureForm?.esign?.formStatus ==
                                        'manual upload' ? (
                                            <Typography>Disclosure Statement Uploaded</Typography>
                                        ) : (
                                            <Typography>Disclosure Statement Signed</Typography>
                                        )}
                                    </Step>
                                </>
                            )}

                            {requiresRei && (
                                <Step
                                    indicator={
                                        <StepIndicator
                                            variant={
                                                FormStage.created <= formStage ||
                                                (reiForm && !form && reiForm)
                                                    ? 'solid'
                                                    : 'soft'
                                            }
                                            color={
                                                !requiresDisclosure || disclosureComplete
                                                    ? 'primary'
                                                    : 'neutral'
                                            }
                                        >
                                            {formStage != FormStage.none || (reiForm && !form && reiForm) ? (
                                                <Check />
                                            ) : undefined}
                                        </StepIndicator>
                                    }
                                    sx={{
                                        '&::after': {
                                            ...((FormStage.created <= formStage ||
                                                (reiForm && !form && reiForm)) && {
                                                bgcolor: 'primary.solidBg',
                                            }),
                                        },
                                    }}
                                >
                                    <Typography>Create in Realworks</Typography>
                                </Step>
                            )}

                            <Step
                                indicator={
                                    <StepIndicator
                                        variant={
                                            formStage >= FormStage.created ||
                                            (requiresRei && !form && listing?.[formType]?.esign)
                                                ? 'solid'
                                                : 'soft'
                                        }
                                        color={
                                            !requiresRei ||
                                            formStage >= FormStage.created ||
                                            (requiresRei && !form && listing?.[formType]?.rei)
                                                ? 'primary'
                                                : 'neutral'
                                        }
                                    >
                                        {(
                                            requiresRei
                                                ? formStage >= FormStage.created
                                                : formStage != FormStage.none
                                        ) ? (
                                            <Check />
                                        ) : undefined}
                                    </StepIndicator>
                                }
                                sx={{
                                    '&::after': {
                                        ...(formStage >= FormStage.created && {
                                            bgcolor: 'primary.solidBg',
                                        }),
                                    },
                                }}
                            >
                                <Typography>
                                    {manualFileUpload ? 'Upload' : `Create eSign document`}
                                </Typography>
                            </Step>
                            <Step
                                indicator={
                                    <StepIndicator
                                        variant={FormStage.sent <= formStage ? 'solid' : 'soft'}
                                        color={FormStage.sent <= formStage + 1 ? 'primary' : 'neutral'}
                                    >
                                        {formStage >= FormStage.sent ? <Check /> : undefined}
                                    </StepIndicator>
                                }
                                sx={{
                                    '&::after': {
                                        ...(formStage >= FormStage.sent && {
                                            bgcolor: 'primary.solidBg',
                                        }),
                                    },
                                }}
                            >
                                <Typography>Send</Typography>
                            </Step>

                            {(form && 'actions' in form
                                ? sortBy(action => {
                                      const status = getActionStatus(action)
                                      return status === 'SIGNED' || status === 'signed' ? 0 : 1
                                  }, form.actions as any[])
                                : undefined
                            )?.map((action, index) => {
                                const actionId = getActionId(action)
                                const status = getActionStatus(action)
                                const name = getActionName(action)

                                return (
                                    <Step
                                        key={actionId}
                                        indicator={
                                            <StepIndicator
                                                variant={
                                                    status == 'SIGNED' || status === 'signed'
                                                        ? 'solid'
                                                        : 'soft'
                                                }
                                                color={
                                                    status == 'SIGNED' || status === 'signed'
                                                        ? 'primary'
                                                        : 'neutral'
                                                }
                                            >
                                                {status == 'SIGNED' || status === 'signed' ? (
                                                    <Check />
                                                ) : undefined}
                                            </StepIndicator>
                                        }
                                    >
                                        <Typography>{name} Signature</Typography>
                                    </Step>
                                )
                            }) ??
                                (form as AnnatureForm)?.recipients?.map(signer => (
                                    <Step
                                        key={signer.id}
                                        indicator={
                                            <StepIndicator
                                                variant={
                                                    signer.status == 'completed' ||
                                                    signer.status == 'declined'
                                                        ? 'solid'
                                                        : 'soft'
                                                }
                                                color={
                                                    signer.status == 'completed'
                                                        ? 'primary'
                                                        : signer.status == 'declined'
                                                          ? 'danger'
                                                          : 'neutral'
                                                }
                                            >
                                                {signer.status == 'completed' ? (
                                                    <Check />
                                                ) : signer.status == 'declined' ? (
                                                    <Cancel />
                                                ) : signer.status == 'delivered' ? (
                                                    <MoveToInbox />
                                                ) : undefined}
                                            </StepIndicator>
                                        }
                                        sx={{
                                            '&::after': {
                                                ...(signer.status == 'completed' && {
                                                    bgcolor: 'primary.solidBg',
                                                }),
                                            },
                                        }}
                                    >
                                        <Stack>
                                            <Typography>{signer.name} Signature</Typography>
                                            {signer.declined_reason && (
                                                <Typography level="body-xs">
                                                    reason: {signer.declined_reason}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Step>
                                )) ??
                                roleNames.map(roleName => (
                                    <Step
                                        key={roleName}
                                        indicator={
                                            <StepIndicator
                                                variant="soft"
                                                color="neutral"
                                            />
                                        }
                                    >
                                        <Stack>
                                            <Typography>{roleName} Signature</Typography>
                                        </Stack>
                                    </Step>
                                ))}
                        </Stepper>
                    </>
                )}
            </>
        )
    }

    console.log({ formStage })

    return (
        <>
            <HeaderSection />
            <BodySection />
            {previewPdf?.url && (
                <PdfModal
                    title={title}
                    pdf={previewPdf.url}
                    onClose={() => setPreviewPdf(undefined)}
                    fileName={`${formatAddress(listing.listingAddress) || 'listing'}_${formType}.pdf`}
                    //hideDownloadButton={form?.formStatus != 'created in rei' && formStage != FormStage.completed}
                    withAuth={previewPdf.useAuth}
                    isRawPdf={previewPdf.isRawPdf}
                />
            )}
            <IframeModal
                title={getFormName(formType)}
                url={editUrl}
                onClose={() => setEditUrl(undefined)}
                signingService={signingService}
                formType={formType}
                listingId={listing.listingId}
            />
            <UploadFormModal
                open={showUploadForm}
                onClose={url => {
                    setShowUploadForm(false)
                    if (url) {
                        console.log('url', url)
                        setEditUrl(url)
                    }
                }}
                formType={formType}
                signingService={signingService}
            />
            <AbandonFormModal
                open={showAbandonFormDialog != undefined}
                onClose={() => setShowAbandonFormDialog(undefined)}
                formType={showAbandonFormDialog || formType}
            />
            <AddAttachmentModal
                open={showAddAttachmentsModal}
                onClose={data => {
                    if (data) {
                        setEditUrl(data.editUrl)
                    }
                    setShowAddAttachmentsModal(false)
                }}
                //setEditUrl={setEditUrl}
                formType={formType}
            />
            <ManualFormUploadModal
                open={showManualUploadModal != undefined}
                close={() => setShowManualUploadModal(undefined)}
                formType={showManualUploadModal!}
            />
        </>
    )
}

export default DocusignFormStepper
